import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { track } from "../lib/analytics";

export type ParsedDoc = {
  amountAED: number | null;
  dueDate?: string;
  issuer?: string;
  lineItems?: Array<{ label: string; amountAED?: number }>;
};

/**
 * InvoiceUpload component.
 * Uploads file to Supabase Storage, then calls the server-side invoice-parse edge function.
 * The client NEVER parses the invoice or supplies the amount itself.
 */
export default function InvoiceUpload({
  onParsed
}: { onParsed: (doc: ParsedDoc) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      // Get current user for folder scoping
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError("Please sign in to upload invoices.");
        return;
      }

      // Upload to Supabase Storage under user's folder
      const filePath = `${user.id}/${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('invoices')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Upload error:', uploadError);
        setError("Failed to upload file. Please try again.");
        return;
      }

      // Call server-side parser
      const { data, error: parseError } = await supabase.functions.invoke('invoice-parse', {
        body: { filePath },
      });

      if (parseError) {
        console.error('Parse error:', parseError);
        setError("Sorry, we couldn't read that file. Try a clear PDF/JPG/PNG.");
        return;
      }

      const parsed = data as ParsedDoc;
      onParsed(parsed);

      // Track successful document parsing
      track("ai_doc_parsed", {
        amountAED: parsed.amountAED,
        issuer: parsed.issuer,
        hasLineItems: !!parsed.lineItems?.length
      });
    } catch (e: any) {
      setError("Sorry, we couldn't read that file. Try a clear PDF/JPG/PNG.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border p-3">
      <div className="text-sm font-medium mb-2">Have an invoice or quote? (Optional)</div>
      <input
        type="file"
        accept="application/pdf,image/*"
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="text-sm"
      />
      {busy && <div className="text-xs text-gray-500 mt-2">Uploading & reading document…</div>}
      {error && <div className="text-xs text-red-600 mt-2">{error}</div>}
      <div className="text-[11px] text-gray-500 mt-2">
        We parse invoice totals only. Don't upload sensitive medical records. See Privacy.
        <br />
        We'll extract amount & due date to tailor plans.
      </div>
    </div>
  );
}
